/**
 * /docs/signing-flow — How resolutions are signed on Elreign.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { LegalLayout } from '../../components/layouts/legal-layout';

export const Route = createFileRoute('/docs/signing-flow')({
  component: SigningFlowPage,
});

const TOC = [
  { anchor: 'principles',         label: 'Design principles' },
  { anchor: 'draft',              label: '1 · Draft the resolution' },
  { anchor: 'review',             label: '2 · Trustee review' },
  { anchor: 'fingerprint',        label: '3 · Out-of-band fingerprint' },
  { anchor: 'sign',               label: '4 · Sign with biometric' },
  { anchor: 'seal',               label: '5 · Seal on the audit chain' },
  { anchor: 'external',           label: 'External signers' },
  { anchor: 'recovery',           label: 'What happens if a step fails' },
];

function SigningFlowPage() {
  useEffect(() => {
    document.title = 'The signing flow · Elreign documentation';
    const meta = document.querySelector('meta[name="description"]') ?? (() => {
      const m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute(
      'content',
      'How resolutions are signed on Elreign: draft, trustee review, out-of-band fingerprint, biometric signature, and chain seal. Plus external signers and failure recovery.',
    );
  }, []);

  return (
    <LegalLayout heading="The signing flow." lastUpdated="17 May 2026" toc={TOC}>
      <p>
        Every resolution on Elreign passes through the same five-step flow. The steps cannot be
        reordered, skipped, or merged. This guide describes each step from the perspective of the
        administrator drafting the resolution and the trustee signing it.
      </p>

      <h2 id="principles">Design principles</h2>
      <p>
        The signing flow is built on three principles. First, the trustee must see the resolution
        body as it will be sealed. Body-swap is defended against by re-deriving the SHA-256 hash
        of the resolution on the server at the moment of signing. Second, the trustee must
        confirm out of band that they are the person signing: a fingerprint is sent over a
        channel separate from the platform itself. Third, the signature must be inseparable from
        the resolution body, the trustee&apos;s identity, and the chain hash of the preceding
        event.
      </p>

      <h2 id="draft">1 · Draft the resolution</h2>
      <p>
        From the trust&apos;s page, choose <strong>Draft resolution</strong>. The administrator
        selects a resolution type (distribution, trustee change, investment authorisation,
        general) and either fills the structured fields or writes free-form. The platform
        validates that the resolution is consistent with the trust deed: distributions cannot
        exceed available assets, trustee changes must satisfy quorum requirements, and so on.
      </p>

      <h2 id="review">2 · Trustee review</h2>
      <p>
        The draft is published to the trustees named on the deed. Each trustee receives an in-app
        notification and an email link to review. Review opens the resolution in a sheet that
        shows the full body, the trust&apos;s current state, and the chain hash this resolution
        will follow. Trustees may comment, request amendments, or proceed to sign.
      </p>

      <h2 id="fingerprint">3 · Out-of-band fingerprint</h2>
      <p>
        When a trustee chooses to sign, the platform displays a six-character fingerprint derived
        from the resolution&apos;s SHA-256 hash. The same fingerprint is sent over a separate
        channel (typically SMS to the trustee&apos;s registered mobile number) by the platform.
        The trustee compares the on-screen fingerprint to the one received on their device. They
        must match for signing to proceed.
      </p>
      <p>
        If the fingerprints do not match, the trustee should cancel the signing flow and report
        the mismatch to their firm. A fingerprint mismatch is a signal that the resolution body
        has been altered between draft and signing, and is treated as a security event with
        automatic notification to the principal administrator.
      </p>

      <h2 id="sign">4 · Sign with biometric</h2>
      <p>
        With the fingerprint confirmed, the trustee signs by Face ID, Touch ID, or platform
        biometric. The signature event captures the trustee&apos;s authenticated identity, the
        device public key, a re-derivation of the resolution hash on the server, the fingerprint
        match confirmation, and the timestamp to the second.
      </p>

      <h2 id="seal">5 · Seal on the audit chain</h2>
      <p>
        Once all required trustees have signed, the resolution is sealed. The chain writer
        computes a new chain hash linking the resolution to the preceding event on this
        trust&apos;s audit chain. The sealed resolution is written to WORM storage and is no
        longer editable by anyone. Subsequent amendments are made by a new resolution that
        supersedes the prior one; the prior remains on the chain forever.
      </p>

      <h2 id="external">External signers</h2>
      <p>
        External signers (auditors, professional executors, beneficiaries, outside counsel who
        are not Elreign users) sign through a single-use, time-boxed magic link. No account is
        created. The link page shows the issuing firm and the resolution, then verifies the
        signer&apos;s identity with a one-time code sent to their registered email and/or mobile
        number (entered on the page, with a limited number of attempts before the link locks).
      </p>
      <p>
        After the code is verified, the signer must read the full resolution text on screen and
        explicitly confirm it before they can continue: the signature is bound to the exact text
        they reviewed by re-deriving its SHA-256 hash on the server, the same body-swap defence
        used for internal signing. If the resolution was edited after the link was issued, signing
        is paused and the signer is asked to obtain a fresh link rather than sign stale text.
      </p>
      <p>
        Once the signer confirms, the signature is recorded on-server as an ordinary electronic
        signature under SA ECTA s13(3). No external signing provider is involved. Trust
        resolutions are not Schedule 2 instruments, so this ordinary, OTP-verified,
        body-hash-pinned signature is valid at law; it is not an Advanced Electronic Signature
        (AES, ECTA s4 + Schedule 2). They may also decline, with an optional reason; the declining
        decision and reason are recorded and the requesting firm is notified.
        The external signature is bound to the same audit chain as every internal signature. The
        link expires on the date shown on the signing page; an expired or already-used link cannot
        be reused.
      </p>

      <h2 id="recovery">What happens if a step fails</h2>
      <p>
        If the SMS does not arrive, the trustee may request a re-send up to three times. If the
        biometric fails, the trustee may retry up to five times before the device is temporarily
        locked from signing. A fingerprint mismatch cancels the flow and alerts the principal
        administrator. A chain-seal failure (rare; typically a transient infrastructure event)
        queues the resolution for an automatic re-attempt and notifies the administrator if
        re-attempts continue to fail.
      </p>

      <p style={{ marginTop: 32 }}>
        <Link to="/docs">Back to documentation hub →</Link>
      </p>
    </LegalLayout>
  );
}
